import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton, withStyles } from 'vopay';
import { BodyText } from 'UI';
import { UsersPage } from 'shared/users';
import useAccountBearer from '@/components/Entities/hooks/useApi/useAccountBearer';
import AccountContext from '@/components/Entities/Accounts/AccountContext';
import { usePartnerData } from '@/providers/PartnerData.provider';
import AccountScopedApiProvider from './AccountScopedApiProvider';
import useAccountUsersHostProps from './useAccountUsersHostProps';
import styles from './UsersTab.module.scss';

const cn = withStyles(styles);

/**
 * The account page's Account Portal Users tab (spec 5.6.4, ID-13429): the shared Users feature — the
 * account portal's User Management — scoped to this account by its own bearer, under the partner's
 * heading.
 */
export default function UsersTab() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Tabs' });
  const { accountId } = useParams<{ accountId: string }>();
  const { bearer, hasSettled } = useAccountBearer(accountId);
  const { balances } = useContext(AccountContext);

  // The same rule as the Sub-Accounts tab: an account whose entitlement was later switched off keeps the
  // sub-accounts it already has, so a role can still be applied to them.
  const hasSubAccounts = useMemo(() => {
    const listedAccount = balances.find((item) => item.AccountID === accountId);

    return Boolean(listedAccount?.SubAccountsEnabled) || (listedAccount?.SubAccounts ?? []).length > 0;
  }, [balances, accountId]);

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const hostProps = useAccountUsersHostProps({
    bearer,
    hasSubAccounts,
    title: t('accountPortalUsers', { defaultValue: 'Account Portal Users' }),
  });

  // On a deep link the token is looked up after mount; the feature fetches on mount, so it must not
  // render until the token is there to scope that fetch.
  if (!bearer && !hasSettled) return <Skeleton height={320} data-testid="usersTabLoading" />;

  // The lookup has answered and there is no token to scope the feature with — see TransactionsTab.
  if (!bearer) {
    return (
      <BodyText data-testid="usersTabUnavailable">
        {t('unavailable', { defaultValue: "This account's data could not be loaded." })}
      </BodyText>
    );
  }

  return (
    <div className={cn('tab')}>
      {/* The feature already signs its calls with the account's bearer; the scoped api is here for the
          Viewer refusal on every user and role write, a second line behind the hidden actions. */}
      <AccountScopedApiProvider bearer={bearer} readOnly={isRoleViewOnly}>
        <UsersPage {...hostProps} />
      </AccountScopedApiProvider>
    </div>
  );
}
